import { CBSocketClient, SocketClient } from "cbcore-ts"
import { IF, NO, UIButton, UIColor, UIRoute, UITextView, UIView, UIViewController, YES } from "uicore-ts"
import { RHFocusManager } from "../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHPageFocusManager } from "../Custom components/RH/Keyboard navigation/RHPageFocusManager"
import { RHAlertBannerView } from "../Custom components/RH/RHAlertBannerView"
import { RHButton } from "../Custom components/RH/RHButton"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHButtons, RHColors, RHFont, RHSpacing } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHForgotPasswordViewTokens, RHLoginViewTokens } from "../Custom components/RH/Utils_slop/RHViewTokens"
import { RHTextField } from "../Custom components/RH/RHTextField"
import { LoginViewController } from "./LoginViewController"


export class ForgotPasswordViewController extends UIViewController {
    
    readonly titleLabel = new UITextView(
        this.view.elementID + "TitleLabel",
        // UITextView.type.header2
        UITextView.type.header4
    ).configuredWithObject({
        text: "Unustasid salasõna?",
        backgroundColor: UIColor.transparentColor,
        textColor: RHColors.textPrimary,
        textAlignment: UITextView.textAlignment.center,
        style: {
            fontFamily: RHFont.familyBase,
            fontSize: RHFont.sizeXXLarge,
            fontWeight: RHForgotPasswordViewTokens.titleFontWeight,
            lineHeight: RHFont.lineHeightHeading
        }
    }).addedAsSubviewToView(this.view)
    
    readonly explanationView = new UITextView().addedAsSubviewToView(this.view).configuredWithObject({
        text: "Sisesta oma e-posti aadress ja saadame sulle salasõna lähtestamise lingi.",
        textAlignment: UITextView.textAlignment.center
    })
    
    readonly statusBannerView = new RHAlertBannerView({
        textAlignment: UITextView.textAlignment.center
    }).addedAsSubviewToView(this.view)
    
    readonly emailTextField = new RHTextField().addedAsSubviewToView(this.view).configuredWithObject({
        placeholderText: "E-posti aadress",
        autocompleteEnabled: YES,
        controlEventTargetAccumulator: {
            EnterDown: (sender, event) => {
                this.submitButton.sendControlEventForKey(UIButton.controlEvent.EnterDown, event)
            }
        }
    })
    
    readonly submitButton = new RHButton().addedAsSubviewToView(this.view).configuredWithObject({
        titleLabel: { text: "Saada link" },
        variant: "success",
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: async (sender, event) => {
                await this._submitForgotPasswordRequest()
            }
        }
    })
    
    readonly backToLoginButton = new RHButton().addedAsSubviewToView(this.view).configuredWithObject({
        titleLabel: { text: "Tagasi sisselogimise lehele" },
        variant: "link",
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: (sender, event) => {
                UIRoute.currentRoute
                    .routeByRemovingComponentNamed(ForgotPasswordViewController.routeComponentName)
                    .routeWithViewControllerComponent(LoginViewController, {})
                    .apply()
            }
        }
    })
    
    readonly bottomView = new UIView().addedAsSubviewToView(this.view)
    
    override _focusManager = new RHFocusManager(() => [
        this.emailTextField,
        this.submitButton,
        this.backToLoginButton
    ])
    
    
    constructor(view: UIView) {
        
        super(view)
        
        this.view.configureWithObject({
            backgroundColor: RHColor.backgroundColor
        })
        
    }
    
    
    static override readonly routeComponentName = "forgot-password"
    
    
    override async viewWillAppear() {
        
        await super.viewWillAppear()
        
        RHPageFocusManager.shared.registerContentControllerManager(this._focusManager)
        
    }
    
    
    override async viewWillDisappear() {
        
        await super.viewWillDisappear()
        
        RHPageFocusManager.shared.deregisterContentControllerManager(this._focusManager)
        
    }
    
    
    private async _submitForgotPasswordRequest() {
        
        const email = this.emailTextField.text.trim()
        
        if (!email) {
            this.statusBannerView.configureWithObject({
                text: "Palun sisesta e-posti aadress.",
                alertType: RHAlertBannerView.type.Error
            })
            this.view.setNeedsLayoutUpToRootView()
            return
        }
        
        this.submitButton.enabled = NO
        
        try {

            await SocketClient.RequestPasswordReset(
                { email },
                CBSocketClient.completionPolicy.firstOnly
            )

            // Always show success regardless of whether email exists (prevent user enumeration)
            this.statusBannerView.configureWithObject({
                text: "Kui see e-posti aadress on registreeritud, saadame sulle salasõna lähtestamise lingi.",
                alertType: RHAlertBannerView.type.Success
            })
            this.emailTextField.text = ""
            
        }
        catch (error) {
            
            this.statusBannerView.configureWithObject({
                text: "Viga lingi saatmisel. Palun proovi hiljem uuesti.",
                alertType: RHAlertBannerView.type.Error
            })
            
        }
        finally {
            
            this.submitButton.enabled = YES
            this.view.setNeedsLayoutUpToRootView()
            
        }
        
    }
    
    
    override layoutViewSubviews() {
        
        super.layoutViewSubviews()
        
        const padding = this.core.paddingLength
        const bounds = this.view.contentBoundsWithInset(padding)
        const contentBounds = bounds.rectangleWithMaxWidth(RHLoginViewTokens.authContentMaxWidth, 0.5)
        
        this.titleLabel.frame = contentBounds.rectangleWithHeight(this.titleLabel)
        
        this.explanationView.frame = this.titleLabel.frame.rectangleForNextRow(
            padding,
            this.explanationView
        )
        
        const bannerHeight = IF(this.statusBannerView.text)(() =>
            this.statusBannerView.intrinsicContentHeight(contentBounds.width)
        ).ELSE(() =>
            0
        )
        this.statusBannerView.frame = this.explanationView.frame.rectangleForNextRow(
            IF(this.statusBannerView.text)(() => padding).ELSE(() => 0),
            bannerHeight
        )
        
        this.emailTextField.frame = this.statusBannerView.frame.rectangleForNextRow(
            padding,
            [
                this.emailTextField.intrinsicContentHeight(contentBounds.width),
                RHForgotPasswordViewTokens.fieldMinHeight
            ].max()
        )
        
        const submitButtonWidth = [
            [
                this.submitButton.titleLabel.intrinsicContentWidth(RHForgotPasswordViewTokens.submitButtonHeight) +
                RHButtons.contentPadding * 2 +
                RHSpacing.xLarge,
                RHForgotPasswordViewTokens.submitButtonMinWidth
            ].max(),
            RHForgotPasswordViewTokens.submitButtonMaxWidth,
            contentBounds.width
        ].min()

        const submitButtonRowFrame = this.emailTextField.frame.rectangleForNextRow(
            padding,
            RHForgotPasswordViewTokens.submitButtonHeight
        )
        this.submitButton.frame = submitButtonRowFrame.rectangleWithWidth(submitButtonWidth, 0.5)
        
        this.backToLoginButton.frame = submitButtonRowFrame.rectangleForNextRow(
            padding,
            RHForgotPasswordViewTokens.submitButtonHeight
        )
        
        this.bottomView.frame = this.backToLoginButton.frame.rectangleForNextRow(padding, 1)
        
    }
    
}
